import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Button, Input } from "reactstrap";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

import { hasPermission } from "../Utils/UtilityFunctions";
import { getAllPO } from "../../slices/PO/GetPO/thunk";
import { stockinRequestPO } from "../../slices/PO/ApprovalRequestPO/thunk";
import { getNotification } from "../../slices/Notification/GetNotification/thunk";
import { useColumnSettings } from "../Utils/useColumnSettings";
import { formatCurrency } from "../Utils/Currency";

import POForm from "./POForm";
import POStockInForm from "./POStockInForm";
import POTable from "./Pages/POTable";
import CommonModal from "../Utils/CommonModal";

const STATUS_FILTER = "pending, waiting for approval, approved, completed";

const formatPO = (po, index) => ({
  id: index + 1,
  poNumber: po.orderid,
  location: po.tolocationname || "-",
  locationId: po.tolocation || "-",
  items: po.items?.length || 0,
  totalAmt:
    po.items?.reduce((sum, item) => sum + Number(item.totalprice || 0), 0) || 0,
  itemData: (po.items || []).map((item) => ({
    id: item.id,
    sku: item.sku,
    itemid: item.itemid,
    item: item.itemname,
    toLocation: po.tolocationname || "-",
    toLocationId: po.tolocation,
    orderedquantity: item.orderedquantity,
    approvedquantity: item.approvedquantity || 0,
    notificationid: item.notificationid,
    netQty: item.itemcount,
    unitPrice: item.unitprice,
    total: item.totalprice,
    status:
      item.status === "pending"
        ? "Pending"
        : item.status === "waiting for approval"
          ? "Waiting Approval"
          : item.status === "approved"
            ? "Approved"
            : item.status === "completed"
              ? "Completed"
              : item.status,
    rawStatus: item.status,
    action:
      item.status === "pending"
        ? "request"
        : item.status === "waiting for approval"
          ? "approve"
          : item.status === "approved"
            ? "stockin"
            : item.status === "completed"
              ? "completed"
              : null,
  })),
});

function POListings() {
  const dispatch = useDispatch();
  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap,
  );
  const roleName = localStorage.getItem("roleName");
  const operatorName = localStorage.getItem("tradingName");

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)]),
  );
  const purchaseorderPermission = hasPermission(
    permissionsMap,
    "transactions",
    "purchaseorder",
  );
  const notificationsPermission = hasPermission(permissionsMap, "monitoringandcontrol", "notifications");

  const poList = useSelector((state) => state.GetPO.poList || []);

  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [allPOData, setAllPOData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isApprove, setIsApprove] = useState(false);

  const [stockInModalOpen, setStockInModalOpen] = useState(false);
  const [stockInData, setStockInData] = useState(null);

  const [commonModalOpen, setCommonModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalType, setModalType] = useState("");

  useEffect(() => {
    dispatch(
      getAllPO(
        pagination.pageSize,
        pagination.pageIndex + 1,
        STATUS_FILTER,
        setLoading,
      ),
    );
  }, [dispatch, pagination.pageSize, pagination.pageIndex]);

  useEffect(() => {
    const formattedData = (poList.purchaseOrders || []).map(formatPO);

    setAllPOData((prev) => {
      const merged = [...prev, ...formattedData];
      return merged.filter(
        (item, index, self) =>
          index === self.findIndex((x) => x.poNumber === item.poNumber),
      );
    });
  }, [poList]);

  const poData = useMemo(() => {
    const formattedData = (poList.purchaseOrders || []).map(formatPO);

    if (!searchValue.trim()) return formattedData;

    return allPOData.filter((po) => {
      const search = searchValue.toLowerCase();
      return (
        po.poNumber?.toLowerCase().includes(search) ||
        po.location?.toLowerCase().includes(search) ||
        po.itemData?.some(
          (item) =>
            item.item?.toLowerCase().includes(search) ||
            item.sku?.toLowerCase().includes(search),
        )
      );
    });
  }, [poList, searchValue, allPOData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "poNumber",
        header: "Purchase Order Number",
        size: 220,
        minSize: 220,
        maxSize: 220,
      },
      {
        accessorKey: "location",
        header: "Location",
        size: 180,
        minSize: 180,
        maxSize: 180,
      },
      {
        accessorKey: "items",
        header: "No. of Items",
        size: 120,
        minSize: 120,
        maxSize: 120,
      },
      {
        accessorKey: "totalAmt",
        header: "Total Amount",
        size: 150,
        minSize: 150,
        maxSize: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
    ],
    [],
  );

  const { CustomViewModal } = useColumnSettings("purchaseorder", columns);

  const refreshPOList = () =>
    dispatch(
      getAllPO(
        pagination.pageSize,
        pagination.pageIndex + 1,
        STATUS_FILTER,
        setLoading,
      ),
    );

  const toggleModal = () => {
    setModalOpen((prev) => !prev);
    if (modalOpen) {
      setEditData(null);
      setIsEdit(false);
      setIsApprove(false);
    }
  };

  const handleEdit = (rowData, parentRow) => {
    setIsEdit(true);
    setEditData({
      orderId: parentRow.poNumber,
      locationName: {
        label: parentRow.location,
        value: parentRow.location,
      },
      status: {
        label: parentRow.status,
        value: parentRow.status,
      },
      items: [
        {
          itemName: { label: rowData.item, value: rowData.item },
          sku: rowData.sku,
          orderedQty: rowData.orderedquantity,
          approvedQty: rowData.orderedquantity,
          unitPrice: rowData.unitPrice,
          itemId: rowData.itemid,
          itemCount: rowData.netQty,
          total: rowData.total,
          notificationid: rowData.notificationid,
        },
      ],
    });
    setModalOpen(true);
  };

  const handleRequest = (rowData, parentRow) => {
    setSelectedRow({ rowData, parentRow });
    setModalType("request");
    setCommonModalOpen(true);
  };

  const handleApprove = (rowData, parentRow) => {
    handleEdit(rowData, parentRow);
    setIsApprove(true);
  };

  const handleStockIn = (rowData, parentRow) => {
    setStockInData({
      itemName: rowData.item,
      itemId: rowData.itemid,
      sku: rowData.sku,
      quantity: rowData.netQty,
      stockInQty: rowData.approvedquantity || rowData.netQty,
      toLocation: rowData.toLocation,
      tolocationId: rowData.toLocationId,
      poNumber: parentRow.poNumber,
    });
    setStockInModalOpen(true);
  };

  const handleApprovalAction = () => {
    const { rowData, parentRow } = selectedRow;

    if (modalType === "request") {
      const payload = {
        orderid: parentRow.poNumber,
        tolocation: parentRow.locationId,
        tolocationname: parentRow.location,
        items: [
          {
            itemid: rowData.itemid,
            itemname: rowData.item,
            quantity: rowData.netQty,
            stockinquantity: rowData.orderedquantity,
            message: "Request for Stockin",
            type: "Stockin",
          },
        ],
      };

      dispatch(
        stockinRequestPO(operatorName, payload, setLoading, () => {
          setCommonModalOpen(false);
          if (notificationsPermission) dispatch(getNotification());
          refreshPOList();
        }),
      );
    }
  };

  return (
    <React.Fragment>
      <div id="csms">
        <Container fluid>
          <Row>
            {/* Page header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="rv-page-title m-0">Order List</h5>
            </div>

            {/* Search + action bar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
              <div className="d-flex align-items-center w-100 gap-2">
                <Input
                  type="text"
                  placeholder="Type to search here"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setSearchValue(searchText);
                  }}
                  style={{
                    maxWidth: "500px",
                    height: "36px",
                    borderRadius: "4px",
                  }}
                />
                <Button
                  className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 text-nowrap"
                  onClick={() => setSearchValue(searchText)}
                >
                  <i className="ri-search-line label-icon align-middle fs-16 me-1 fw-bold"></i>
                  <span className="ps-2 ms-2 me-1 fw-bold">Search</span>
                </Button>
              </div>

              <div className="d-flex align-items-center">
                {purchaseorderPermission?.has("export") && (
                  <Button className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 text-nowrap mx-1">
                    <i className="ri-download-line label-icon secondary-label-icon align-middle fs-16 me-2 fw-bold"></i>
                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">Export</span>
                  </Button>
                )}
                {purchaseorderPermission?.has("create") && (
                  <Button
                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={toggleModal}
                  >
                    <i className="ri-add-fill label-icon align-middle fs-16 me-1 fw-bold"></i>
                    <span className="ps-2 ms-2 me-1 fw-bold">
                      Add Purchase Order
                    </span>
                  </Button>
                )}
              </div>
            </div>

            {/* Main table */}
            <Col lg={12}>
              <POTable
                poData={poData}
                totalCount={poList?.totalCount || 0}
                pagination={pagination}
                onPaginationChange={setPagination}
                loading={loading}
                columns={columns}
                purchaseorderPermission={purchaseorderPermission}
                onEdit={handleEdit}
                onRequest={handleRequest}
                onApprove={handleApprove}
                onStockIn={handleStockIn}
              />
            </Col>

            {CustomViewModal()}
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{ borderBottom: "1px solid #FD7545", padding: "16px 20px" }}
        >
          <span style={{ color: "#FD7545", fontWeight: "600" }}>
            {isApprove
              ? "Approve Purchase Order"
              : isEdit
                ? "Update Purchase Order"
                : "Add Purchase Order"}
          </span>
        </ModalHeader>
        <ModalBody
          className="overflow-auto"
          style={{ maxHeight: "80vh", padding: "20px 20px 0px 20px" }}
        >
          <POForm
            closeModal={toggleModal}
            editData={editData}
            isEdit={isEdit}
            isApprove={isApprove}
            refreshPOList={refreshPOList}
          />
        </ModalBody>
      </Modal>

      <Modal
        isOpen={stockInModalOpen}
        toggle={() => setStockInModalOpen(false)}
        centered
        size="md"
      >
        <ModalHeader
          toggle={() => setStockInModalOpen(false)}
          style={{ borderBottom: "1px solid #FD7545", padding: "16px 20px" }}
        >
          <span style={{ color: "#FD7545", fontWeight: "600" }}>
            Stock In Product
          </span>
        </ModalHeader>
        <ModalBody>
          <POStockInForm
            stockInData={stockInData}
            closeModal={() => setStockInModalOpen(false)}
            refreshPOList={refreshPOList}
          />
        </ModalBody>
      </Modal>

      <CommonModal
        open={commonModalOpen}
        toggle={() => setCommonModalOpen(false)}
        modalHeader={modalType === "request" ? "Request Approval" : ""}
        messageForPopUp={
          modalType === "request"
            ? "Are you sure you want to request approval for this product?"
            : ""
        }
        buttonText={modalType === "request" ? "Request Approval" : ""}
        modalAction={handleApprovalAction}
        isSave={true}
      />
    </React.Fragment>
  );
}

export default POListings;
